import { nanoid } from 'nanoid';
import type { Relays, RelayMessageHandler, NodeConnections, Node } from '../types.ts';
import type { MeshChannel, MeshSubscriber, MeshAction, MeshEvent, MeshMessage } from '@interopio/gateway/mesh/api';
import type { Logger } from '@interopio/gateway/logging/api';

let instanceId = 0;

type NodeState<T=string> = {
    subscriber: MeshSubscriber<T>,
    users: Set<string>,
    members: Set<string>
};

export default class BridgeMeshChannel implements MeshChannel {
    readonly #logger: Logger;
    readonly #relays: Relays;
    readonly #connections: NodeConnections;
    readonly #state: Map<string, NodeState<unknown>>;
    readonly #keyPrefix: string;

    constructor(relays: Relays,
                nodes: NodeConnections,
                logger: Logger
    ) {
        this.#relays = relays;
        this.#connections = nodes;
        this.#logger = logger;
        this.#state = new Map<string, NodeState<unknown>>();
        this.#keyPrefix = `g.${++instanceId}`;
    }

    get relays(): Relays {
        return this.#relays;
    }
    get connections(): NodeConnections {
        return this.#connections;
    }

    #intervalId: ReturnType<typeof setInterval>;

    subscribe<T = string>(node?: string, subscriber?: MeshSubscriber<T>): string {
        node ??= nanoid();
        const key = `${this.#keyPrefix}-${node}`;
        if (this.#state.has(key)) {
            throw new Error(`already subscribed to node ${node}`);
        }
        this.#state.set(node, {subscriber, users: new Set<string>(), members: new Set<string>()});
        this.#relays.receive(key, { type: 'hello' as const, from: node, to: 'all' });
        this.#relays.add(key, this.createRelayClient(key, node));
        this.#intervalId ??= setInterval(() => this.#announce(), 30000);
        return node;
    }

    private createRelayClient<T>(key: string, node: string): RelayMessageHandler {
        return (_msg: string, command, cb) => {
            switch (command.type) {
                case 'hello':
                case "bye":
                    break;

                case "data": {
                    try {
                        const event: MeshEvent<T> = {
                            type: 'message-received',
                            message: command.data as MeshMessage<T>
                        };
                        this.#state.get(node)?.subscriber(event, node, this);
                    }
                    catch (err) {
                        cb(key, err)
                    }
                    break;
                }
            }
        }
    }

    unsubscribe(node: string): void {
        this.#delete(node);
        this.#relays.remove(`${this.#keyPrefix}-${node}`);
    }

    execute<T = string>(node: string, action: MeshAction<T>): void {
        switch (action.type) {
            case 'publish-message': {
                this.#onMessage(node, action.message as MeshMessage);
                break;
            }
            case 'add-users': {
                this.#onAddUsers(node, action.added);
                break;
            }
            case 'remove-users': {
                this.#onRemoveUsers(node, action.removed);
                break;
            }
        }
    }

    #onMessage(node: string, data: MeshMessage): void {
        const nodes = this.#connections.find(node);
        if (nodes !== undefined) {
            const key = `${this.#keyPrefix}-${node}`;
            const command = { type: 'data' as const, from: node, data };
            nodes.forEach((connection) => {
                const to = connection.node;
                if (to === node) {
                    return;
                }

                try {
                    this.#relays.send(key, to, { ...command, to }, (k, err) => {
                        if (err) {
                            this.#logger.warn(`failed to send message from ${node} to ${to} via ${k}: ${err.message}`);
                        }
                    });
                } catch (err) {
                    this.#logger.warn(`failed to send message from ${node} to ${to} via ${key}: ${(err as Error).message}`);
                }
            });
        }
    }

    #onAddUsers(node: string, usersToAdd: string[]) {
        let shouldAnnounce = false;
        const nodeState = this.#state.get(node);
        const nodeUsers = nodeState?.users;
        if (nodeUsers === undefined) {
            nodeState.users = new Set<string>(usersToAdd);
            shouldAnnounce = true;
        }
        else {
            for (const u of usersToAdd) {
                if (nodeUsers.has(u)) {
                    continue;
                }
                nodeUsers.add(u);
                shouldAnnounce = true;
            }
        }
        if (shouldAnnounce) {
            this.#announce(node);
        }
    }

    #onRemoveUsers(node: string, usersToRemove: string[]) {
        const nodeState = this.#state.get(node);
        const nodeUsers = nodeState?.users;
        if (nodeUsers === undefined) {
            return;
        }
        let shouldAnnounce = false;
        for (const u of usersToRemove) {
            if (!nodeUsers.has(u)) {
                continue;
            }
            nodeUsers.delete(u);
            shouldAnnounce = true;
        }
        if (nodeUsers.size === 0) {
            this.#delete(node);
        }
        else if (shouldAnnounce) {
            this.#announce(node);
        }
    }

    #announce(node?: string) {
        const entries = node === undefined ? this.#state.entries() : [[node, this.#state.get(node)]];
        const nodes: Node[] = Array.from(entries, entryToNode);

        const nodeConnections = this.#connections.announce(nodes);
        for (const {node: n, connect} of nodeConnections) {
            const state = this.#state.get(n);
            if (state === undefined) {
                continue;
            }
            const toRemove = new Set<string>(state.members);
            for (const c of connect) {
                if (c.node === n) {
                    continue;
                }
                toRemove.delete(c.node);
                if (state.members.has(c.node)) {
                    continue;
                }
                state.members.add(c.node);
                state.subscriber({type: 'member-added', node: c.node}, n, this);
            }
            for (const r of toRemove) {
                state.members.delete(r);
                state.subscriber({type: 'member-removed', node: r}, n, this);
            }
        }
    }

    #delete(node: string) {
        this.#state.delete(node);
        this.#connections.remove(node);
    }

    close() {
        for (const node of this.#state.keys()) {
            this.#delete(node);
            this.#relays.remove(`${this.#keyPrefix}-${node}`);
        }
        clearInterval(this.#intervalId)
    }
}

const entryToNode = ([node, state]: [string, NodeState]): Node => {
    const users = Array.from(state?.users);
    const endpoint = `/cluster?node=${node}`;

    return {node, endpoint, users};
}
